import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { eventsAPI } from "../services/schedulerAPI";
import { showToast } from "../components/Toast";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await eventsAPI.list();
      setEvents(data.events || []);
    } catch (error) {
      console.error("Failed to load events:", error);
      showToast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (event) => {
    const link = `${window.location.origin}/e/${event.slug}`;
    navigator.clipboard.writeText(link);
    showToast.success("Event link copied to clipboard!");
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) {
      return;
    }

    try {
      await eventsAPI.delete(eventId);
      showToast.success("Event deleted successfully");
      loadEvents();
    } catch (error) {
      console.error("Failed to delete event:", error);
      showToast.error("Failed to delete event");
    }
  };

  const handlePublishClick = (event) => {
    setSelectedEvent(event);
    setShowPublishModal(true);
  };

  const handlePublishConfirm = async () => {
    if (!selectedEvent) return;

    setPublishing(true);
    try {
      // Validate required fields
      if (!selectedEvent.title || !selectedEvent.start_datetime || !selectedEvent.end_datetime) {
        showToast.error("Event must have title and dates before publishing");
        setPublishing(false);
        setShowPublishModal(false);
        return;
      }

      // Publish the event (backend will handle Zoom meeting creation if enabled)
      await eventsAPI.publish(selectedEvent.id);
      showToast.success("Event published successfully!");
      setShowPublishModal(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      console.error("Failed to publish event:", error);
      showToast.error(error.response?.data?.detail || "Failed to publish event");
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublishClick = (event) => {
    setSelectedEvent(event);
    setShowUnpublishModal(true);
  };

  const handleUnpublishConfirm = async () => {
    if (!selectedEvent) return;

    setPublishing(true);
    try {
      // Unpublish the event (backend will handle cleanup)
      await eventsAPI.update(selectedEvent.id, { status: "draft" });
      showToast.success("Event unpublished successfully");
      setShowUnpublishModal(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      console.error("Failed to unpublish event:", error);
      showToast.error("Failed to unpublish event");
    } finally {
      setPublishing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-100 text-gray-700",
      published: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700",
      completed: "bg-blue-100 text-blue-700",
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading events...</div>
      </div>
    );
  }

  const PublishModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">🚀</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Publish Event?
          </h3>
          <p className="text-gray-600 text-sm">
            This will make your event page public and accessible via a shareable link.
            {selectedEvent?.zoom_enabled && (
              <span className="block mt-2 font-medium text-blue-600">
                A Zoom meeting will be automatically created.
              </span>
            )}
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <h4 className="font-medium text-gray-900 mb-2 text-sm">Event Details:</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <p><strong>Title:</strong> {selectedEvent?.title}</p>
            {selectedEvent?.start_datetime && (
              <p><strong>Start:</strong> {new Date(selectedEvent.start_datetime).toLocaleString()}</p>
            )}
            {selectedEvent?.end_datetime && (
              <p><strong>End:</strong> {new Date(selectedEvent.end_datetime).toLocaleString()}</p>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={handlePublishConfirm}
            disabled={publishing}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {publishing ? "Publishing..." : "Yes, Publish Event"}
          </button>
          <button
            onClick={() => {
              setShowPublishModal(false);
              setSelectedEvent(null);
            }}
            disabled={publishing}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );

  const UnpublishModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">⚠️</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Unpublish Event?
          </h3>
          <p className="text-gray-600 text-sm">
            This will make your event page inaccessible to the public. The event will return to draft status.
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
          <h4 className="font-medium text-yellow-900 mb-2 text-sm">⚠️ Warning:</h4>
          <ul className="space-y-1 text-sm text-yellow-800 list-disc list-inside">
            <li>The public event page will become inaccessible (404)</li>
            <li>Existing shareable links will stop working</li>
            <li>Registered attendees may be notified of cancellation</li>
            {selectedEvent?.zoom_enabled && (
              <li>Associated Zoom meeting will be cancelled</li>
            )}
          </ul>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleUnpublishConfirm}
            disabled={publishing}
            className="w-full px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {publishing ? "Unpublishing..." : "Yes, Unpublish Event"}
          </button>
          <button
            onClick={() => {
              setShowUnpublishModal(false);
              setSelectedEvent(null);
            }}
            disabled={publishing}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Event Pages</h1>
              <p className="text-gray-600 mt-1">
                Create beautiful event landing pages with custom branding
              </p>
            </div>
            <Link
              to="/events/new"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              + Create Event Page
            </Link>
          </div>
        </div>

        {/* Events Grid */}
        {events.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No event pages yet
              </h3>
              <p className="text-gray-600 mb-6">
                Create your first event page to start accepting registrations
              </p>
              <Link
                to="/events/new"
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Create Your First Event Page
              </Link>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Cover Image */}
                {event.cover_image_url ? (
                  <div className="h-40 bg-gray-200 overflow-hidden">
                    <img
                      src={event.cover_image_url}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="h-40 flex items-center justify-center text-4xl"
                    style={{
                      backgroundColor: event.brand_colors?.primary || "#4F46E5",
                    }}
                  >
                    📅
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex-1">
                      {event.title}
                    </h3>
                    {getStatusBadge(event.status)}
                  </div>

                  {event.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {event.description}
                    </p>
                  )}

                  {event.event_date && (
                    <div className="text-sm text-gray-500 mb-4">
                      📅 {new Date(event.event_date).toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/events/${event.id}/edit`)}
                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      >
                        Edit
                      </button>
                      {event.status === "published" && (
                        <button
                          onClick={() => handleCopyLink(event)}
                          className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                        >
                          Copy Link
                        </button>
                      )}
                    </div>

                    {event.status === "draft" && (
                      <button
                        onClick={() => handlePublishClick(event)}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Publish Event
                      </button>
                    )}

                    {event.status === "published" && (
                      <>
                        <a
                          href={`/e/${event.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-center py-2 text-sm text-blue-600 hover:underline"
                        >
                          View Public Page →
                        </a>
                        <button
                          onClick={() => handleUnpublishClick(event)}
                          className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
                        >
                          Unpublish Event
                        </button>
                      </>
                    )}

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Modals */}
        <AnimatePresence>
          {showPublishModal && selectedEvent && <PublishModal />}
        </AnimatePresence>

        <AnimatePresence>
          {showUnpublishModal && selectedEvent && <UnpublishModal />}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Events;
